import React, { memo, useCallback, useMemo } from 'react';
import { Platform, View } from 'react-native';
import TouchableHighlight from 'utils/packages/TouchableHighlight';
import Logo from 'design-system/Logo';
import Text from 'design-system/Text';
import Squircle from 'design-system/Squircle';
import { ProgressBar, Spacer, rem } from 'design-system';
import useStyles from 'hooks/useStyles';
import useColors from 'hooks/useColors';
import createStyleSheets from 'utils/createStyleSheets';
import { Categories } from 'utils/types/navigation';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import useSectors from '../hooks/useSectors';
const InsightsRow = ({ data, total, count, index }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const { sectorInfoMap } = useSectors();
    const { navigateDeprecated } = useMainStackNavigation();
    const renderIcon = useMemo(() => {
        const { category } = data;
        if ('Icon' in category && 'gradientKey') {
            const { Icon, gradientKey } = category;
            return (<Squircle width={rem(46)} height={rem(46)} color={colors.background.dark}>
          <Icon gradientKey={gradientKey}/>
        </Squircle>);
        }
        const iconUrl = 'iconUrl' in data.category ? data.category.iconUrl : undefined;
        return <Logo R44 uri={iconUrl} resizeMode="contain"/>;
    }, [colors.background.dark, data]);
    const percent = useMemo(() => {
        if (total) {
            return Number(((data.value / total) * 100).toFixed(2));
        }
        return 0;
    }, [data.value, total]);
    const containerStyle = useMemo(() => [styles.container, index === 0 && styles.topContainer, index === count - 1 && styles.bottomContainer], [count, index, styles.bottomContainer, styles.container, styles.topContainer]);
    const handlePress = useCallback(() => {
        if ('iconUrl' in data.category) {
            navigateDeprecated('InvestStock', { assetId: data.key });
        }
        else if ('sectorId' in data.category) {
            const { sector, sectorId } = data.category;
            if (sectorId) {
                const infoObj = sectorInfoMap[sector];
                navigateDeprecated('InvestCategoryFilterDetailsScreen', {
                    id: sectorId,
                    title: sector,
                    filterType: Categories.SECTORS,
                    ...infoObj,
                });
            }
        }
    }, [data.category, data.key, sectorInfoMap]);
    const title = useMemo(() => ('name' in data.category ? data.category?.name : data.key), [data.category, data.key]);
    return (<TouchableHighlight hoverOnLight underlayColor={colors.background.underlay} onPress={handlePress}>
      <View style={containerStyle}>
        {renderIcon}
        <Spacer w={16}/>
        <View style={styles.mainContainer}>
          <View style={styles.row}>
            <View style={styles.flex}>
              <Text Text-16 numberOfLines={1}>
                {title}
              </Text>
            </View>
            <Spacer w={8}/>
            <Text Numbers-16>{`${percent}%`}</Text>
          </View>
          <Spacer h={8}/>
          <View style={styles.row}>
            <ProgressBar position={percent} colors={colors.gradients.ultimate.colors}/>
          </View>
        </View>
      </View>
    </TouchableHighlight>);
};
export default memo(InsightsRow);
export const INSIGHTS_ROW_HEIGHT = rem(76);
const styleSet = createStyleSheets((colors) => ({
    container: {
        alignItems: 'center',
        flexDirection: 'row',
        marginHorizontal: rem(16),
        paddingHorizontal: rem(16),
        height: INSIGHTS_ROW_HEIGHT,
        backgroundColor: colors.cards.onDark,
    },
    mainContainer: {
        flex: 1,
        justifyContent: 'center',
    },
    row: {
        flexDirection: 'row',
    },
    topContainer: {
        borderTopLeftRadius: rem(16),
        borderTopRightRadius: rem(16),
        ...(Platform.OS === 'ios' && {
            overflow: 'hidden',
        }),
    },
    bottomContainer: {
        borderBottomLeftRadius: rem(16),
        borderBottomRightRadius: rem(16),
        ...(Platform.OS === 'ios' && {
            overflow: 'hidden',
        }),
    },
    flex: {
        flex: 1,
    },
}));
